import type {
  QuoteDetailPublic,
  QuoteRevisionPublic,
  QuoteTaskDetailPublic,
  QuoteTaskStatus,
} from "@common/client"
import { isComplete } from "./task-values"

const SUBMITTABLE: QuoteTaskStatus[] = ["ready", "approved"]

const SENDABLE: QuoteTaskStatus[] = ["ready_for_customer", "approved"]

export function quoted(quote: QuoteDetailPublic): QuoteTaskDetailPublic[] {
  return quote.active_tasks.filter((task) => task.status !== "excluded")
}

export function toReview(quote: QuoteDetailPublic): QuoteTaskDetailPublic[] {
  return quote.disposition === "review"
    ? quote.active_tasks.filter((task) => task.status === "ready")
    : []
}

export function needingChanges(
  quote: QuoteDetailPublic,
): QuoteTaskDetailPublic[] {
  return quote.active_tasks.filter(
    (task) => task.status === "clarification_requested",
  )
}

export function notSubmittable(
  quote: QuoteDetailPublic,
): QuoteTaskDetailPublic[] {
  return quoted(quote).filter((task) => !SUBMITTABLE.includes(task.status))
}

export function unpriced(quote: QuoteDetailPublic): QuoteTaskDetailPublic[] {
  return quoted(quote).filter((task) => !isComplete(quote, task))
}

export function canSubmit(quote: QuoteDetailPublic): boolean {
  return (
    quoted(quote).length > 0 &&
    notSubmittable(quote).length === 0 &&
    unpriced(quote).length === 0
  )
}

export function sending(quote: QuoteDetailPublic): QuoteTaskDetailPublic[] {
  const status: QuoteTaskStatus =
    quote.disposition === "draft" ? "ready" : "ready_for_customer"
  return quote.active_tasks.filter((task) => task.status === status)
}

export function canSend(quote: QuoteDetailPublic): boolean {
  const ready = sending(quote).length > 0
  if (quote.disposition === "draft") return ready && canSubmit(quote)
  return ready && quoted(quote).every((task) => SENDABLE.includes(task.status))
}

export function wasReviewed(quote: QuoteDetailPublic): boolean {
  return quote.active_revisions.some((revision) => revision.kind === "internal")
}

export function wasSent(quote: QuoteDetailPublic): string | null {
  const sent = quote.active_revisions
    .filter((revision) => revision.kind === "customer" && revision.sent_at)
    .map((revision) => revision.sent_at)
  return sent.reduce<string | null>(
    (first, at) => (first === null || (at && at < first) ? at : first),
    null,
  )
}

export function lastSent(
  quote: QuoteDetailPublic,
): QuoteRevisionPublic | undefined {
  return quote.active_revisions.find(
    (revision) => revision.kind === "customer" && revision.sent_at,
  )
}

export function inQueue(
  quote: QuoteDetailPublic,
  task: QuoteTaskDetailPublic,
): boolean {
  return quote.disposition === "review" && task.status === "ready"
}
